import { Helmet } from "react-helmet-async";
import SustainwizeNavbar from "@/components/landing/SustainwizeNavbar";
import Footer from "@/components/landing/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { CheckCircle, BookOpen, Users, Globe } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { trackFBCustomEvent } from "@/components/FacebookPixel";
import SustainwizeFeaturesSection from "@/components/landing/SustainwizeFeaturesSection";
import SustainwizeHowItWorksSection from "@/components/landing/SustainwizeHowItWorksSection";
import SustainwizeVVBSection from "@/components/landing/SustainwizeVVBSection";
import SustainwizeTestimonialsSection from "@/components/landing/SustainwizeTestimonialsSection";
import SustainwizeArticlesSection from "@/components/landing/SustainwizeArticlesSection";
import sustainwizeCtaBackground from "@/assets/sustainwize-cta-background.png";
const Sustainwize = () => {
  const { t, language } = useLanguage();

  const metadata = {
    en: {
      title: "Sustainwize.io | Sustainability data platform",
      description: "Empower hotels to meet global sustainability standards. Sustainwize.io is an automated sustainability data platform developed by Carbonwize Co., Ltd.",
      locale: "en_US"
    },
    th: {
      title: "Sustainwize.io | แพลตฟอร์มข้อมูลความยั่งยืน",
      description: "ช่วยให้โรงแรมบรรลุมาตรฐานความยั่งยืนระดับสากล Sustainwize.io คือแพลตฟอร์มข้อมูลความยั่งยืนอัตโนมัติที่พัฒนาโดย Carbonwize Co., Ltd.",
      locale: "th_TH"
    }
  };

  return <>
      <Helmet>
        <title>{metadata[language].title}</title>
        <meta name="description" content={metadata[language].description} />
        <link rel="canonical" href="https://carbonwize.io/sustainwize" />
        
        {/* hreflang tags for multilingual SEO */}
        <link rel="alternate" hrefLang="en" href="https://carbonwize.io/en/sustainwize" />
        <link rel="alternate" hrefLang="th" href="https://carbonwize.io/th/sustainwize" />
        <link rel="alternate" hrefLang="x-default" href="https://carbonwize.io/sustainwize" />
        
        {/* Open Graph - Language Aware */}
        <meta property="og:title" content={metadata[language].title} />
        <meta property="og:description" content={metadata[language].description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://carbonwize.io/sustainwize" />
        <meta property="og:image" content="https://carbonwize.io/sustainwize-og-thumbnail.png" />
        <meta property="og:locale" content={metadata[language].locale} />
        <meta property="og:site_name" content="Carbonwize" />
        
        {/* Structured Data - BreadcrumbList */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://carbonwize.io"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Sustainwize",
                "item": "https://carbonwize.io/sustainwize"
              }
            ]
          })}
        </script>
      </Helmet>
      
      <SustainwizeNavbar />
      
      <main className="pt-[65px]">
        {/* Hero Section */}
        <section className="relative py-24 lg:py-32 overflow-hidden min-h-[500px] flex items-center">
          {/* Background Image - Thailand beach with sustainable hotel */}
          <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=1920&h=1080&fit=crop')`
        }} />
          {/* Black gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70" />
          
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <span className="inline-block px-4 py-2 rounded-full bg-white/10 text-white text-sm font-medium mb-6 backdrop-blur-sm">
                {t("sustainwize.badge")}
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
                Sustainability Data Management Platform<br />
                <span className="text-[#fd6158]">Built for Hotels</span>
              </h1>
              <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
                {t("sustainwize.heroSubtitle")}
              </p>
              
              {/* Badges */}
              <div className="flex flex-wrap justify-center gap-4 mb-8">
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20">
                  <BookOpen className="w-4 h-4 text-[#fd6158]" />
                  <span className="text-white text-sm font-medium">{t("sustainwize.badgeGSTC")}</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20">
                  <Users className="w-4 h-4 text-[#fd6158]" />
                  <span className="text-white text-sm font-medium">{t("sustainwize.badgeTeam")}</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20">
                  <Globe className="w-4 h-4 text-[#fd6158]" />
                  <span className="text-white text-sm font-medium">{t("sustainwize.badgeStandards")}</span>
                </div>
              </div>
              
              <div className="flex justify-center">
                <Button size="lg" className="bg-[#fd6158] hover:bg-[#fd6158]/90 text-white px-8" asChild>
                  <Link to="/sustainwize/contact-us" onClick={() => trackFBCustomEvent('CTAClick', { button: 'book_demo', source: 'sustainwize_hero' })}>{t("sustainwize.bookDemo")}</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Why Sustainwize Section */}
        <SustainwizeFeaturesSection />

        {/* How It Works Section */}
        <SustainwizeHowItWorksSection />

        {/* Key Benefits Section */}
        <section className="py-24 lg:py-32 bg-[#FFF5F0] relative overflow-hidden">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#373530] mb-4">
                {t("sustainwize.benefitsTitle")}
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
              {/* Time Efficiency */}
              <div className="group p-8 lg:p-10 rounded-3xl bg-gradient-to-b from-white to-[#f9fafb] border border-border/50 shadow-sm hover:shadow-xl hover:border-[#fd6158]/30 transition-all duration-300">
                <div className="w-20 h-20 rounded-2xl bg-[#fd6158]/10 flex items-center justify-center mx-auto mb-6 group-hover:bg-[#fd6158]/20 transition-colors duration-300">
                  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-10 h-10">
                    <circle cx="32" cy="32" r="28" stroke="#fd6158" strokeWidth="2" strokeDasharray="4 2" />
                    <circle cx="32" cy="32" r="4" fill="#fd6158" />
                    <line x1="32" y1="32" x2="32" y2="16" stroke="#fd6158" strokeWidth="2" strokeLinecap="round" />
                    <line x1="32" y1="32" x2="44" y2="32" stroke="#fd6158" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </div>
                <h3 className="text-xl lg:text-2xl font-bold text-[#373530] mb-6 text-center">{t("sustainwize.benefit1Title")}</h3>
                <ul className="space-y-4">
                  <li className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-[#fd6158] flex-shrink-0 mt-0.5" />
                    <p className="text-muted-foreground leading-relaxed"><strong className="text-[#373530]">{t("sustainwize.benefit1Point1")}</strong> {t("sustainwize.benefit1Point1Desc")}</p>
                  </li>
                  <li className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-[#fd6158] flex-shrink-0 mt-0.5" />
                    <p className="text-muted-foreground leading-relaxed"><strong className="text-[#373530]">{t("sustainwize.benefit1Point2")}</strong> {t("sustainwize.benefit1Point2Desc")}</p>
                  </li>
                </ul>
              </div>

              {/* Financial Savings */}
              <div className="group p-8 lg:p-10 rounded-3xl bg-gradient-to-b from-white to-[#f9fafb] border border-border/50 shadow-sm hover:shadow-xl hover:border-[#fd6158]/30 transition-all duration-300">
                <div className="w-20 h-20 rounded-2xl bg-[#fd6158]/10 flex items-center justify-center mx-auto mb-6 group-hover:bg-[#fd6158]/20 transition-colors duration-300">
                  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-10 h-10">
                    <path d="M32 8L32 56" stroke="#fd6158" strokeWidth="2" strokeLinecap="round" />
                    <path d="M20 20L32 8L44 20" stroke="#fd6158" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" transform="rotate(180 32 14)" />
                    <circle cx="32" cy="44" r="12" stroke="#fd6158" strokeWidth="2" />
                    <text x="32" y="48" textAnchor="middle" fill="#fd6158" fontSize="12" fontWeight="bold">$</text>
                  </svg>
                </div>
                <h3 className="text-xl lg:text-2xl font-bold text-[#373530] mb-6 text-center">{t("sustainwize.benefit2Title")}</h3>
                <ul className="space-y-4">
                  <li className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-[#fd6158] flex-shrink-0 mt-0.5" />
                    <p className="text-muted-foreground leading-relaxed"><strong className="text-[#373530]">{t("sustainwize.benefit2Point1")}</strong> {t("sustainwize.benefit2Point1Desc")}</p>
                  </li>
                  <li className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-[#fd6158] flex-shrink-0 mt-0.5" />
                    <p className="text-muted-foreground leading-relaxed"><strong className="text-[#373530]">{t("sustainwize.benefit2Point2")}</strong> {t("sustainwize.benefit2Point2Desc")}</p>
                  </li>
                  <li className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-[#fd6158] flex-shrink-0 mt-0.5" />
                    <p className="text-muted-foreground leading-relaxed"><strong className="text-[#373530]">{t("sustainwize.benefit2Point3")}</strong> {t("sustainwize.benefit2Point3Desc")}</p>
                  </li>
                </ul>
              </div>

              {/* Revenue & Market Growth */}
              <div className="group p-8 lg:p-10 rounded-3xl bg-gradient-to-b from-white to-[#f9fafb] border border-border/50 shadow-sm hover:shadow-xl hover:border-[#fd6158]/30 transition-all duration-300">
                <div className="w-20 h-20 rounded-2xl bg-[#fd6158]/10 flex items-center justify-center mx-auto mb-6 group-hover:bg-[#fd6158]/20 transition-colors duration-300">
                  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-10 h-10">
                    <rect x="8" y="40" width="10" height="16" stroke="#fd6158" strokeWidth="2" />
                    <rect x="27" y="28" width="10" height="28" stroke="#fd6158" strokeWidth="2" />
                    <rect x="46" y="16" width="10" height="40" stroke="#fd6158" strokeWidth="2" />
                    <circle cx="51" cy="10" r="6" stroke="#fd6158" strokeWidth="2" />
                    <text x="51" y="13" textAnchor="middle" fill="#fd6158" fontSize="8" fontWeight="bold">$</text>
                    <path d="M46 16L51 10" stroke="#fd6158" strokeWidth="2" />
                    <path d="M51 10L56 6" stroke="#fd6158" strokeWidth="2" strokeLinecap="round" />
                    <path d="M52 6H56V10" stroke="#fd6158" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <h3 className="text-xl lg:text-2xl font-bold text-[#373530] mb-6 text-center">{t("sustainwize.benefit3Title")}</h3>
                <ul className="space-y-4">
                  <li className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-[#fd6158] flex-shrink-0 mt-0.5" />
                    <p className="text-muted-foreground leading-relaxed"><strong className="text-[#373530]">{t("sustainwize.benefit3Point1")}</strong> {t("sustainwize.benefit3Point1Desc")}</p>
                  </li>
                  <li className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-[#fd6158] flex-shrink-0 mt-0.5" />
                    <p className="text-muted-foreground leading-relaxed"><strong className="text-[#373530]">{t("sustainwize.benefit3Point2")}</strong> {t("sustainwize.benefit3Point2Desc")}</p>
                  </li>
                  <li className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-[#fd6158] flex-shrink-0 mt-0.5" />
                    <p className="text-muted-foreground leading-relaxed"><strong className="text-[#373530]">{t("sustainwize.benefit3Point3")}</strong> {t("sustainwize.benefit3Point3Desc")}</p>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Customer Stories Section - Hidden */}

        <SustainwizeVVBSection />

        {/* Articles Section */}
        <SustainwizeArticlesSection />

        {/* Pricing CTA Section */}
        <section id="pricing" className="py-24 lg:py-32 bg-background relative overflow-hidden">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="max-w-4xl mx-auto">
              <div className="relative px-6 py-12 sm:p-12 lg:p-16 overflow-hidden rounded-3xl" style={{
              backgroundImage: `url(${sustainwizeCtaBackground})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}>
                
                <div className="relative text-left">
                  <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4 leading-tight">
                    Lead with <span style={{ color: '#2ec27c' }}>Sustainability Data</span>
                  </h2>
                  <p className="text-base sm:text-lg text-white/80 mb-8 max-w-2xl">
                    {t("sustainwize.ctaDescriptionLine1")}
                    <br />
                    {t("sustainwize.ctaDescriptionLine2")}
                  </p>
                  
                  {/* Benefits */}
                  <div className="flex flex-wrap gap-4 lg:gap-6 mb-10">
                    <div className="flex items-center gap-2 text-white/90">
                      <CheckCircle className="w-5 h-5 text-accent" />
                      <span className="text-sm font-medium">{t("sustainwize.ctaBenefit1")}</span>
                    </div>
                    <div className="flex items-center gap-2 text-white/90">
                      <CheckCircle className="w-5 h-5 text-accent" />
                      <span className="text-sm font-medium">{t("sustainwize.ctaBenefit2")}</span>
                    </div>
                    <div className="flex items-center gap-2 text-white/90">
                      <CheckCircle className="w-5 h-5 text-accent" />
                      <span className="text-sm font-medium">{t("sustainwize.ctaBenefit3")}</span>
                    </div>
                  </div>
                  
                  {/* CTA Buttons */}
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button size="lg" className="bg-accent hover:bg-accent/90 text-accent-foreground px-8" asChild>
                      <Link to="/sustainwize/contact-us" onClick={() => trackFBCustomEvent('CTAClick', { button: 'book_demo', source: 'sustainwize_cta' })}>
                        {t("sustainwize.bookDemo")}
                        <span className="ml-2">→</span>
                      </Link>
                    </Button>
                    <Button size="lg" variant="outline" className="border-white/30 text-white bg-transparent hover:bg-white/10 px-8" asChild>
                      <Link to="/become-partner" onClick={() => trackFBCustomEvent('CTAClick', { button: 'become_partner', source: 'sustainwize_cta' })}>{t("sustainwize.partnerWithUs")}</Link>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>;
};
export default Sustainwize;